import { SignedIn, SignedOut } from "@clerk/clerk-react";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import EnhancedSignIn from "../../features/auth/components/EnhancedSignIn";
import Button from "../atoms/Button";
import Logo from "../atoms/Logo";
import UserMenu from "./UserMenu";

/**
 * NavigationBar - Main application navigation header
 *
 * @param {Object} props
 * @param {boolean} props.showEnvWarning - Whether to show environment warning
 */
const NavigationBar = ({ showEnvWarning = false }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);

  // Close mobile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    }

    if (isMobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Handle keyboard shortcuts for menu
  useEffect(() => {
    function handleKeyDown(event) {
      // Close menu on Escape key
      if (event.key === "Escape" && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu when screen resizes to desktop
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth >= 640) {
        // sm breakpoint in Tailwind
        setIsMobileMenuOpen(false);
      }
    }

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {showEnvWarning && (
        <div className="bg-yellow-800 text-white px-4 py-2">
          <div className="max-w-7xl mx-auto">
            <p>
              ⚠️ Running with fallback configuration. For production use, set
              environment variables.
            </p>
          </div>
        </div>
      )}

      <nav className="bg-[#1a1e21] shadow-sm relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Logo />
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                <Link
                  to="/"
                  className="border-transparent text-gray-100 hover:text-[#4db1e0] inline-flex items-center px-1 pt-1 border-b-2 hover:border-[#4db1e0]/30 text-sm font-medium transition-all duration-200"
                >
                  Dashboard
                </Link>
                <SignedIn>
                  <Link
                    to="/intake"
                    className="border-transparent text-gray-300 hover:text-[#4db1e0] inline-flex items-center px-1 pt-1 border-b-2 hover:border-[#4db1e0]/30 text-sm font-medium transition-all duration-200"
                  >
                    Intake
                  </Link>
                  <Link
                    to="/workshop"
                    className="border-transparent text-gray-300 hover:text-[#4db1e0] inline-flex items-center px-1 pt-1 border-b-2 hover:border-[#4db1e0]/30 text-sm font-medium transition-all duration-200"
                  >
                    Workshop
                  </Link>
                </SignedIn>
                <Link
                  to="/help"
                  className="border-transparent text-gray-300 hover:text-[#4db1e0] inline-flex items-center px-1 pt-1 border-b-2 hover:border-[#4db1e0]/30 text-sm font-medium transition-all duration-200"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    className="h-4 w-4 mr-1"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  Help
                </Link>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <SignedIn>
                <UserMenu />
              </SignedIn>
              <SignedOut>
                <div className="hidden sm:flex sm:items-center sm:space-x-2">
                  <EnhancedSignIn>
                    <Button variant="primary">Sign In</Button>
                  </EnhancedSignIn>
                  <Link to="/sign-up" className="ml-2">
                    <Button variant="secondary">Sign Up</Button>
                  </Link>
                </div>
              </SignedOut>

              {/* Mobile menu button */}
              <button
                className="sm:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-[#262b30] transition-colors duration-200 focus:outline-none"
                aria-expanded={isMobileMenuOpen}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle mobile menu"
              >
                <span className="sr-only">Open main menu</span>
                {/* Hamburger icon */}
                {!isMobileMenuOpen ? (
                  <svg
                    className="block h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                ) : (
                  // Close (X) icon
                  <svg
                    className="block h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          ref={mobileMenuRef}
          className={`${
            isMobileMenuOpen
              ? "translate-x-0 opacity-100"
              : "translate-x-full opacity-0"
          } sm:hidden fixed inset-0 z-50 transition-all duration-300 ease-in-out flex`}
          aria-hidden={!isMobileMenuOpen}
        >
          <div
            className="bg-[#0f1418]/80 flex-grow"
            onClick={closeMobileMenu}
          ></div>
          <div className="bg-[#1a1e21] shadow-xl w-64 transform transition-all duration-300 ease-in-out h-full overflow-y-auto">
            <div className="px-4 pt-5 pb-6 space-y-6">
              <div className="flex items-center justify-between">
                <Logo className="text-lg" />
                <button
                  className="rounded-md text-gray-400 hover:text-white focus:outline-none"
                  onClick={closeMobileMenu}
                >
                  <span className="sr-only">Close menu</span>
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="mt-6">
                <nav className="grid gap-y-8">
                  <Link
                    to="/"
                    className="-m-3 p-3 flex items-center rounded-md hover:bg-[#262b30] text-gray-100 hover:text-[#4db1e0] transition-colors duration-200"
                    onClick={closeMobileMenu}
                  >
                    <svg
                      className="h-5 w-5 mr-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                      />
                    </svg>
                    Dashboard
                  </Link>
                  <SignedIn>
                    <Link
                      to="/intake"
                      className="-m-3 p-3 flex items-center rounded-md hover:bg-[#262b30] text-gray-300 hover:text-[#4db1e0] transition-colors duration-200"
                      onClick={closeMobileMenu}
                    >
                      <svg
                        className="h-5 w-5 mr-2"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      Intake
                    </Link>
                    <Link
                      to="/workshop"
                      className="-m-3 p-3 flex items-center rounded-md hover:bg-[#262b30] text-gray-300 hover:text-[#4db1e0] transition-colors duration-200"
                      onClick={closeMobileMenu}
                    >
                      <svg
                        className="h-5 w-5 mr-2"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                      Workshop
                    </Link>
                  </SignedIn>
                  <Link
                    to="/help"
                    className="-m-3 p-3 flex items-center rounded-md hover:bg-[#262b30] text-gray-300 hover:text-[#4db1e0] transition-colors duration-200"
                    onClick={closeMobileMenu}
                  >
                    <svg
                      className="h-5 w-5 mr-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                    Help & Shortcuts
                  </Link>
                </nav>
              </div>
              <SignedOut>
                <div className="mt-6 flex flex-col space-y-3">
                  <EnhancedSignIn>
                    <Button variant="primary" className="w-full justify-center">
                      Sign In
                    </Button>
                  </EnhancedSignIn>
                  <Link
                    to="/sign-up"
                    className="w-full"
                    onClick={closeMobileMenu}
                  >
                    <Button
                      variant="secondary"
                      className="w-full justify-center"
                    >
                      Sign Up
                    </Button>
                  </Link>
                </div>
              </SignedOut>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default NavigationBar;
